import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslate } from 'react-admin';
import { Box, CircularProgress, Drawer, Typography } from '@mui/material';
import { APIProvider, Map, useMap, InfoWindow, ControlPosition } from '@vis.gl/react-google-maps';
import { styled } from '@mui/material/styles';
import DeviceStateIndicator from '@/components/device/DeviceStateIndicator.jsx';
import { useDrawer } from '@/composables/useDrawer';
import { ClusteredCollectionPointMarkers, createCollectionPointMarkerContent } from '@/components/collectionPoint/map/ClusteredCollectionPointMarkers.jsx';
import { useCollectionPointDevices } from '@/components/collectionPoint/map/useCollectionPointDevices';
import { DrawerLink } from '@/components/global/drawers/DrawerLink';
import { getDrawerContent, getModalDialogClass } from '@/components/global/list/CustomList';
import StateIndicator from '@/components/global/map/StateIndicator.jsx';
import { normalizeKey } from '@/helpers/format/normalizeStrings';
import { Palette } from '@/themes/themeOverrides/Palette';
import NotificationBox from '@/components/global/fields/NotificationBox.jsx';

const StyledMapBox = styled(Box)({
    '&.map-box': {
        position: 'relative',
        width: '100%',
        height: 'calc(98vh - 230px)',
        overflow: 'hidden',
        borderRadius: '16px',
        '& .gm-style-iw-chr': {
            display: 'none',
        },
        '& .gm-style-iw-d': {
            padding: '0',
            overflow: 'hidden !important',
        },
        '& .gm-style-iw.gm-style-iw-c': {
            padding: '0',
        },
    },
});

const StyledCollectionPointInfo = styled(Box, {
    shouldForwardProp: prop => prop !== 'hasDevices',
})(({ hasDevices }) => ({
    width: '100%',
    borderBottom: hasDevices ? `2px solid ${Palette.gray.gray100}` : 'none',
    paddingBottom: hasDevices ? '8px' : '0',
}));

const StyledCollectionPointId = styled(Typography)({
    fontSize: '14px',
    lineHeight: '16px',
    fontWeight: '600',
    display: 'block',
    marginBottom: '4px',
});

const StyledCollectionPointText = styled(Typography)({
    fontSize: '11px',
    lineHeight: '16px',
    fontWeight: '400',
    display: 'block',
    marginBottom: '4px',
});

const DeviceStateIndicatorStyles = {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '0',
    width: '100%',
    '& .state-details': {
        width: '100%',
        justifyContent: 'space-between',
    },
};

const MapClickHandler = ({ onMapClick }) => {
    const map = useMap();
    useEffect(() => {
        if (!map) return;
        const listener = map.addListener('click', () => onMapClick?.());
        return () => listener && listener.remove();
    }, [map, onMapClick]);
    return null;
};

const MapDeviceDrawer = () => {
    const { isDrawerOpen, elementId, type, closeAllDrawers, closeDrawer } = useDrawer();

    if (!isDrawerOpen || !type || !elementId) return null;

    return (
        <Drawer anchor='right' open={isDrawerOpen} onClose={closeAllDrawers} PaperProps={{ className: getModalDialogClass() }}>
            <Box className='custom-dialog-right__inner'>{getDrawerContent(type, elementId, closeDrawer)}</Box>
        </Drawer>
    );
};

const SelectionHandler = ({ selection, markersIndex, collectionPointData, onOpen }) => {
    const map = useMap();

    useEffect(() => {
        if (!map || !selection || !markersIndex) return;

        const { type, value } = selection;

        const centerAndZoom = (lat, lng, zoom = 15) => {
            if (Number.isFinite(lat) && Number.isFinite(lng)) {
                map.panTo({ lat, lng });
                map.setZoom(zoom);
            }
        };

        const fitBoundsForItems = items => {
            if (items.length === 0) return;

            const bounds = new google.maps.LatLngBounds();
            items.forEach(p => {
                if (Number.isFinite(p.latitude) && Number.isFinite(p.longitude)) {
                    bounds.extend({ lat: p.latitude, lng: p.longitude });
                }
            });

            if (!bounds.isEmpty()) {
                map.fitBounds(bounds, 64);
            }
        };
        const handleSingleItem = collectionPoint => {
            const marker = markersIndex.collectionPointIdToMarker.get(String(collectionPoint.id));
            if (marker) {
                onOpen(collectionPoint, marker);
            }
            centerAndZoom(collectionPoint.latitude, collectionPoint.longitude);
        };

        const handleMultipleItems = items => {
            fitBoundsForItems(items);
            onOpen(null, null);
        };

        if (type === 'CollectionPoint') {
            const marker = markersIndex.customIdToMarker.get(normalizeKey(value));
            const collectionPoint = (collectionPointData || []).find(p => normalizeKey(p?.customId) === normalizeKey(value));
            if (marker && collectionPoint) {
                onOpen(collectionPoint, marker);
                centerAndZoom(collectionPoint.latitude, collectionPoint.longitude);
            }
            return;
        }

        if (type === 'Miasto') {
            const city = normalizeKey(value);
            const items = (collectionPointData || []).filter(p => normalizeKey(p?.city || p?.address?.city) === city);
            if (items.length === 1) {
                handleSingleItem(items[0]);
            } else if (items.length > 1) {
                handleMultipleItems(items);
            }
            return;
        }

        if (type === 'Grupa') {
            const groupId = String(value);
            const items = (collectionPointData || []).filter(p => Array.isArray(p.devices) && p.devices.some(d => String(d?.group?.id) === groupId));
            if (items.length === 1) {
                handleSingleItem(items[0]);
            } else if (items.length > 1) {
                handleMultipleItems(items);
            }
        }
    }, [map, selection, markersIndex, collectionPointData, onOpen]);

    return null;
};

export const CollectionPointMapComponent = ({ collectionPointData, googleMapsApiKey, mapId, selection, isLoadingMore = false }) => {
    const translate = useTranslate();
    const [selectedCollectionPoint, setSelectedCollectionPoint] = useState(null);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [markersIndex, setMarkersIndex] = useState(null);
    const handleOpen = useCallback((collectionPoint, marker) => {
        setSelectedCollectionPoint(collectionPoint || null);
        setSelectedMarker(marker || null);
    }, []);
    const handleMarkerClick = useCallback((collectionPoint, marker) => {
        setSelectedCollectionPoint(collectionPoint);
        setSelectedMarker(marker);
    }, []);
    const { devices: pointDevices, isLoading: isDevicesLoading, error: devicesError } = useCollectionPointDevices(selectedCollectionPoint?.id);

    useEffect(() => {
        if (!selectedMarker || !pointDevices) return;
        selectedMarker.content = createCollectionPointMarkerContent({
            ...selectedCollectionPoint,
            devices: pointDevices,
        });
    }, [selectedMarker, pointDevices, selectedCollectionPoint]);

    return (
        <>
            <StyledMapBox className='map-box'>
                <APIProvider apiKey={googleMapsApiKey} libraries={['marker']}>
                    <Map
                        mapId={mapId}
                        defaultZoom={6.5}
                        defaultCenter={{ lat: 52.191111, lng: 19.355417 }}
                        gestureHandling={'greedy'}
                        disableDefaultUI
                        style={{
                            width: '100%',
                            height: '100%',
                        }}
                        fullscreenControl={true}
                        zoomControl={true}
                        options={{
                            zoomControl: true,
                            fullscreenControl: true,
                            zoomControlOptions: { position: ControlPosition.RIGHT_BOTTOM },
                            fullscreenControlOptions: { position: ControlPosition.RIGHT_BOTTOM },
                        }}
                    >
                        <MapClickHandler
                            onMapClick={() => {
                                setSelectedCollectionPoint(null);
                                setSelectedMarker(null);
                            }}
                        />
                        <ClusteredCollectionPointMarkers
                            collectionPointData={collectionPointData}
                            onMarkersReady={setMarkersIndex}
                            onMarkerClick={handleMarkerClick}
                        />
                        <SelectionHandler selection={selection} markersIndex={markersIndex} collectionPointData={collectionPointData} onOpen={handleOpen} />
                        {selectedCollectionPoint && selectedMarker && (
                            <InfoWindow
                                anchor={selectedMarker}
                                onCloseClick={() => {
                                    setSelectedCollectionPoint(null);
                                    setSelectedMarker(null);
                                }}
                                style={{
                                    padding: '0',
                                }}
                            >
                                <Box sx={{ '& a:focus-visible': { outline: 'none', boxShadow: 'none' } }}>
                                    <StateIndicator>
                                        <DrawerLink
                                            resource='collection-point'
                                            source={selectedCollectionPoint.id}
                                            action='show'
                                            relative
                                            label={
                                                <StyledCollectionPointInfo hasDevices={(pointDevices?.length ?? 0) > 0}>
                                                    <StyledCollectionPointId variant='caption'>{selectedCollectionPoint.customId}</StyledCollectionPointId>
                                                    <StyledCollectionPointText variant='caption'>{selectedCollectionPoint.address}</StyledCollectionPointText>
                                                    <StyledCollectionPointText variant='caption'>{`${selectedCollectionPoint.postalCode} ${selectedCollectionPoint.city}`}</StyledCollectionPointText>
                                                </StyledCollectionPointInfo>
                                            }
                                            sx={{
                                                width: '100%',
                                            }}
                                        />
                                        {isDevicesLoading && (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
                                                <CircularProgress size={16} sx={{ color: Palette.yellow.yellow400 }} />
                                            </Box>
                                        )}
                                        {devicesError && (
                                            <Typography variant='caption' fontSize='11px'>
                                                {translate('common.map.collectionPointLoadError', { message: devicesError.message })}
                                            </Typography>
                                        )}
                                        {pointDevices?.length > 0 && (
                                            <DeviceStateIndicator
                                                record={pointDevices}
                                                stateLabel={`${translate('resources.device.fields.status')}: `}
                                                className='state-inner'
                                                sx={{
                                                    ...DeviceStateIndicatorStyles,
                                                }}
                                            />
                                        )}
                                        {!isDevicesLoading && !devicesError && pointDevices?.length === 0 && (
                                            <StyledCollectionPointText variant='caption' sx={{ paddingTop: '8px' }}>
                                                {translate('common.map.noDevices')}
                                            </StyledCollectionPointText>
                                        )}
                                    </StateIndicator>
                                </Box>
                            </InfoWindow>
                        )}
                    </Map>
                </APIProvider>
                {isLoadingMore && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 2,
                            pointerEvents: 'none',
                            maxWidth: 'calc(100% - 32px)',
                            '& .notification-box': {
                                backgroundColor: 'white !important',
                            },
                        }}
                    >
                        <NotificationBox variant='warning' icon={<CircularProgress size={20} sx={{ color: Palette.yellow.yellow400 }} />}>
                            {translate('common.map.loadingMoreCollectionPoints')}
                        </NotificationBox>
                    </Box>
                )}
            </StyledMapBox>
            <MapDeviceDrawer />
        </>
    );
};

CollectionPointMapComponent.propTypes = {
    collectionPointData: PropTypes.arrayOf(PropTypes.object),
    googleMapsApiKey: PropTypes.string,
    mapId: PropTypes.string,
    selection: PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        type: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    isLoadingMore: PropTypes.bool,
};

export default CollectionPointMapComponent;
